/**
 * utils.js contains validation functions for multiple types of data.
 */

import sanitizeHtml from "sanitize-html";

/**
 * Sanitize a string - remove all HTML tags
 * @param {*} str - the string to sanitize
 * @returns the sanitized string
 */
export function sanitizeData(str) {
  return sanitizeHtml(str);
}

/**
 * Sanitize all string properties of an object
 * @param {*} obj - the object to sanitize its string properties
 * @returns the passed object, with its string properties sanitized
 */
export function sanitizeObjectData(obj) {
  const sanitizedObj = {};
  for (const prop in obj) {
    if (typeof obj[prop] === "string") {
      sanitizedObj[prop] = sanitizeData(obj[prop]);
    } else {
      sanitizedObj[prop] = obj[prop];
    }
  }
  return sanitizedObj;
}

/**
 * Sanitize and validate a string. If the passed value is not a string, an error is thrown.
 * @param {*} value - the string to sanitize and validate.
 * @param {*} paramName - parameter name used in case of an error.
 * @returns the sanitized string.
 */
export function validateString(value, paramName) {
  const sanitizedValue = sanitizeData(value);
  if (typeof sanitizedValue !== "string") {
    throw new Error(`${paramName} must be a string.`);
  }
  return sanitizedValue;
}

/**
 * Sanitize and validate a non-empty string. If the passed value is not a string, or if it is an empty string (after sanitization), an error is thrown.
 * @param {*} value - the string to sanitize and validate.
 * @param {*} paramName - parameter name used in case of an error.
 * @returns the sanitized string.
 */
export function validateNonEmptyString(value, paramName) {
  const sanitizedValue = sanitizeData(value);
  if (typeof sanitizedValue !== "string" || !sanitizedValue.trim()) {
    throw new Error(`${paramName} must be a non-empty string.`);
  }

  return sanitizedValue;
}

// Email validation regex
export const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Policy: Length between 8 and 15 characters.
 * At least one special character from the given list.
 * At least two uppercase letters.
 * At least two lowercase letters.
 * At least one digit.
 * no character appearing more than twice
 * no consecutive repeated characters
 * @param {*} password
 * @returns
 */
export const validatePassword = (password) => {
  const regex =
    /^(?=.*[!@#$%^&*()])(?=(?:.*[A-Z]){2})(?=(?:.*[a-z]){2})(?=.*\d)[A-Za-z\d!@#$%^&*()]{8,15}$/;

  if (!regex.test(password)) {
    return "Password length should be between 8 and 15 characters. It should contain at least two uppercase letters, two lowercase letters, one digit, and one special character from the list: !@#$%^&*()";
  }

  const charCount = {};
  for (let i = 0; i < password.length; i++) {
    const char = password[i];
    if (charCount[char]) {
      charCount[char]++;
    } else {
      charCount[char] = 1;
    }

    // Check if any character appears more than twice
    if (charCount[char] > 2) {
      return "No character should appear more than twice.";
    }

    // Check for consecutive repeated characters
    if (i > 0 && password[i] === password[i - 1]) {
      return "No consecutive repeated characters allowed.";
    }
  }

  return null;
};
